import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineRounded";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import { Box, Button, Typography } from "@mui/material";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  /** Human-readable name of the feature, e.g. "Chat", "Projects". */
  name: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary scoped to a single feature. Renders an inline error card
 * inside the feature's own viewport so one broken pane does not take down
 * the whole app. Use the root <ErrorBoundary /> for truly unrecoverable
 * errors (e.g. provider setup failures).
 */
export class FeatureErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[FeatureErrorBoundary:${this.props.name}] caught error:`,
      error,
      info,
    );
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
            p: 3,
            maxWidth: 480,
            bgcolor: "surface.raised",
            border: "1px solid",
            borderColor: "border.default",
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 24 }} />
            <Typography variant="h4" sx={{ color: "text.primary" }}>
              {this.props.name} hit an error
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", wordBreak: "break-word" }}
          >
            {this.state.error?.message ??
              "An unexpected error occurred. The rest of the app is still usable."}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={this.handleRetry}
          >
            Try again
          </Button>
        </Box>
      </Box>
    );
  }
}
